<template>
  <div
    class="scope-switch"
    :style="triggerStyle"
  >
    <button
      ref="triggerRef"
      type="button"
      class="scope-switch__trigger"
      :class="{ 'is-open': visible }"
      aria-haspopup="dialog"
      :aria-expanded="visible"
      :aria-label="proxy.$t('scopeSwitch.title') + '：' + currentName + '，' + currentTypeLabel"
      @click="toggle"
    >
      <img
        :src="logoUrl"
        class="scope-switch__mark"
        alt=""
        aria-hidden="true"
      >
      <span class="scope-switch__name">{{ currentName }}</span>
      <el-icon class="scope-switch__arrow">
        <ArrowDown />
      </el-icon>
    </button>

    <el-dialog
      v-model="visible"
      class="scope-switch-panel"
      :class="{ 'is-expanded': contentVisible }"
      :style="panelStyle"
      :modal-class="['scope-switch-overlay', { 'is-locked': locked }]"
      :title="proxy.$t('scopeSwitch.title')"
      :width="panelWidth"
      top="0"
      append-to-body
      destroy-on-close
      :close-on-click-modal="!locked"
      :close-on-press-escape="!locked"
      :show-close="false"
      :before-close="beforeClose"
      @closed="restoreFocus"
    >
      <template #header="{ titleId }">
        <span
          :id="titleId"
          class="scope-switch__sr-only"
        >{{ proxy.$t('scopeSwitch.title') }}</span>
      </template>
      <div
        class="scope-switch__body"
        :aria-busy="loading || switching"
        @mousemove="handleContentHover"
        @mouseleave="collapseContent"
      >
        <div
          v-if="loading"
          class="scope-switch__state"
          role="status"
        >
          <el-icon class="is-loading">
            <Loading />
          </el-icon>
          <span>{{ proxy.$t('scopeSwitch.loading') }}</span>
        </div>
        <div
          v-else-if="loadFailed"
          class="scope-switch__state"
          role="alert"
        >
          <span>{{ proxy.$t('scopeSwitch.loadFailed') }}</span>
          <el-button @click="loadTenants">
            {{ proxy.$t('scopeSwitch.retry') }}
          </el-button>
        </div>
        <el-empty
          v-else-if="!tenants.length"
          :description="proxy.$t('scopeSwitch.empty')"
        />
        <template v-else>
          <aside
            class="scope-switch__brands"
            :aria-label="proxy.$t('scopeSwitch.choose')"
          >
            <div class="scope-switch__brand-list">
              <button
                v-for="tenant in tenants"
                :key="tenant.tenantId"
                type="button"
                class="scope-switch__brand"
                :class="{ 'is-active': contentVisible && selectedBrand === String(tenant.tenantId) }"
                :aria-expanded="contentVisible && selectedBrand === String(tenant.tenantId)"
                :disabled="locked"
                @mouseenter="selectBrand(tenant)"
                @keydown.right.prevent="selectBrand(tenant)"
                @click="selectBrand(tenant)"
              >
                <img
                  :src="logoUrl"
                  class="scope-switch__mark"
                  alt=""
                  aria-hidden="true"
                >
                <span class="scope-switch__label">{{ tenant.tenantName }}</span>
                <el-icon><ArrowRight /></el-icon>
              </button>
            </div>
            <div
              v-if="recentEntries.length"
              class="scope-switch__recent"
            >
              <button
                type="button"
                class="scope-switch__recent-tab"
                :class="{ 'is-active': contentVisible && selectedBrand === null }"
                :aria-expanded="contentVisible && selectedBrand === null"
                :disabled="locked"
                @mouseenter="selectRecent"
                @keydown.right.prevent="selectRecent"
                @click="selectRecent"
              >
                <span
                  class="scope-switch__recent-mark"
                  aria-hidden="true"
                ><el-icon><Clock /></el-icon></span>
                <span>{{ proxy.$t('scopeSwitch.recent') }}</span>
                <el-icon><ArrowRight /></el-icon>
              </button>
            </div>
          </aside>
          <section
            v-if="contentVisible"
            class="scope-switch__content"
          >
            <template v-if="configPending && switching">
              <div
                class="scope-switch__state"
                role="status"
              >
                <el-icon class="is-loading">
                  <Loading />
                </el-icon>
                <span>{{ proxy.$t('scopeSwitch.switching') }}</span>
              </div>
            </template>
            <template v-else-if="configPending">
              <div
                class="scope-switch__state"
                role="alert"
              >
                <el-icon><Warning /></el-icon>
                <span>{{ proxy.$t('scopeSwitch.configFailed') }}</span>
                <el-button
                  ref="configRetryRef"
                  type="primary"
                  :loading="switching"
                  @click="finishSwitch"
                >
                  {{ proxy.$t('scopeSwitch.retry') }}
                </el-button>
              </div>
            </template>
            <template v-else>
              <el-input
                v-if="selectedBrand !== null"
                v-model="keyword"
                :placeholder="proxy.$t('scopeSwitch.search')"
                :aria-label="proxy.$t('scopeSwitch.search')"
                :prefix-icon="Search"
                :disabled="locked"
                clearable
                class="scope-switch__search"
              />
              <h3
                v-else
                class="scope-switch__recent-title"
              >
                {{ proxy.$t('scopeSwitch.recent') }}
              </h3>
              <p
                v-if="switchError"
                class="scope-switch__error"
                role="alert"
              >
                {{ proxy.$t('scopeSwitch.switchFailed') }}
              </p>
              <div
                class="scope-switch__results"
                aria-live="polite"
              >
                <template v-if="selectedBrand !== null">
                  <section
                    v-for="group in groups"
                    v-show="hasMatches && (!searchKeyword || group.entries.length)"
                    :key="group.type"
                    class="scope-switch__group"
                  >
                    <h3><span aria-hidden="true">·</span> {{ proxy.$t(group.label) }}</h3>
                    <div class="scope-switch__grid">
                      <button
                        v-for="entry in group.entries"
                        :key="entry.key"
                        type="button"
                        class="scope-switch__option"
                        :class="{ 'is-current': isCurrent(entry) }"
                        :aria-current="isCurrent(entry) ? 'true' : undefined"
                        :disabled="locked"
                        @click="switchTo(entry)"
                      >
                        <span class="scope-switch__label">{{ entry.relation.relationName }}</span>
                        <el-icon
                          v-if="isCurrent(entry)"
                          :aria-label="proxy.$t('scopeSwitch.current')"
                        >
                          <Check />
                        </el-icon>
                      </button>
                    </div>
                  </section>
                  <el-empty
                    v-if="!hasMatches"
                    :image-size="72"
                    :description="proxy.$t(searchKeyword ? 'scopeSwitch.noMatches' : 'scopeSwitch.empty')"
                  />
                </template>
                <template v-else>
                  <button
                    v-for="entry in recentEntries"
                    :key="entry.key"
                    type="button"
                    class="scope-switch__option scope-switch__history"
                    :class="{ 'is-current': isCurrent(entry) }"
                    :aria-current="isCurrent(entry) ? 'true' : undefined"
                    :disabled="locked"
                    @click="switchTo(entry)"
                  >
                    <span class="scope-switch__history-name">
                      <span class="scope-switch__label">{{ entry.relation.relationName }}</span>
                      <small class="scope-switch__label">{{ entry.tenant.tenantName }}</small>
                    </span>
                    <span class="scope-switch__history-type">{{ typeLabel(entry.relation.type) }}</span>
                    <el-icon
                      v-if="isCurrent(entry)"
                      :aria-label="proxy.$t('scopeSwitch.current')"
                    >
                      <Check />
                    </el-icon>
                  </button>
                  <el-empty
                    v-if="!recentEntries.length"
                    :image-size="72"
                    :description="proxy.$t('scopeSwitch.noRecent')"
                  />
                </template>
              </div>
              <div
                v-if="switching"
                class="scope-switch__progress"
                role="status"
              >
                <el-icon class="is-loading">
                  <Loading />
                </el-icon>
                {{ proxy.$t('scopeSwitch.switching') }}
              </div>
            </template>
          </section>
        </template>
      </div>
    </el-dialog>
    <Teleport to="body">
      <div
        v-if="switching"
        ref="switchingOverlayRef"
        class="scope-switch-loading"
        role="status"
        :aria-label="proxy.$t('scopeSwitch.switching')"
        aria-live="polite"
        aria-busy="true"
        tabindex="-1"
        @keydown.tab.prevent
      >
        <img
          :src="loadingUrl"
          class="scope-switch-loading__image"
          alt=""
          aria-hidden="true"
        >
      </div>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import { ArrowDown, ArrowRight, Check, Clock, Loading, Search, Warning } from '@element-plus/icons-vue';
import logoUrl from '@/assets/logo/logo.png';
import loadingUrl from '@/assets/logo/loading.svg';
import { getTenantI18nConfig, getTenantList, tenantWitchApi } from '@/api/login';
import type { TenantEntry, TenantRelation, TenantRelationType } from '@/api/types';
import { useUserStore } from '@/store/modules/user';
import variables from '@/assets/styles/variables.module.scss';
import { removeTenantI18nConfigCache, setTenantI18nConfigCache } from '@/utils/i18n';
import router from '@/router';
import { trimUnicodeWhitespace } from '@/utils/whitespace';
import { SCOPE_SWITCH_RECENT_KEY } from '@/utils/auth';
import { getTenantListCache } from '@/utils/tenantListCache';

interface ScopeEntry {
  key: string;
  tenant: TenantEntry;
  relation: TenantRelation;
}

const { proxy } = getCurrentInstance() as ComponentInternalInstance;
const userStore = useUserStore();
const triggerRef = ref<HTMLButtonElement>();
const switchingOverlayRef = ref<HTMLElement>();
const configRetryRef = ref<{ $el: HTMLButtonElement }>();
const brandWidth = ref(Number.parseFloat(variables.sideBarWidth));
const brandPadding = ref(12);
const panelLeft = ref(0);
const triggerOffset = ref(0);
const visible = ref(false);
const contentVisible = ref(false);
const loading = ref(false);
const loadFailed = ref(false);
const switching = ref(false);
const configPending = ref(false);
const switchError = ref(false);
const tenants = ref<TenantEntry[]>([]);
const selectedBrand = ref<string | null>('');
const keyword = ref('');
const searchKeyword = computed(() => trimUnicodeWhitespace(keyword.value).toLocaleLowerCase());
const recentKeys = ref<string[]>([]);
const locked = computed(() => switching.value || configPending.value);
const panelWidth = computed(() => `min(${brandWidth.value + (contentVisible.value ? 398 : 0)}px, calc(100vw - ${panelLeft.value}px))`);
const panelStyle = computed(() => ({
  '--scope-switch-brand-width': `${brandWidth.value}px`,
  '--scope-switch-brand-padding': `${brandPadding.value}px`,
  '--scope-switch-left': `${panelLeft.value}px`,
}));
const triggerStyle = computed(() => ({
  '--scope-switch-trigger-width': `${brandWidth.value - brandPadding.value * 2}px`,
  '--scope-switch-trigger-offset': `${triggerOffset.value}px`,
}));
const syncPanelPosition = () => {
  const layout = triggerRef.value?.closest('.app-wrapper');
  const layoutLeft = Math.max(0, layout?.getBoundingClientRect().left ?? 0);
  const center = layout?.querySelector<HTMLElement>('.centerMianBox');
  const gutter = center ? Number.parseFloat(getComputedStyle(center).paddingLeft) || 0 : 0;
  // 始终靠左并保留菜单标准宽度及留白，收起侧栏时入口和品牌列不随内容区移动。
  panelLeft.value = layoutLeft;
  brandWidth.value = Number.parseFloat(variables.sideBarWidth) + gutter;
  brandPadding.value = window.innerWidth <= 480 ? 8 : 12;
  const navbar = triggerRef.value?.closest<HTMLElement>('.navbar');
  const navbarLeft = (navbar?.getBoundingClientRect().left ?? 0) + (navbar ? Number.parseFloat(getComputedStyle(navbar).paddingLeft) || 0 : 0);
  triggerOffset.value = panelLeft.value + brandPadding.value - navbarLeft;
};
onMounted(syncPanelPosition);
useEventListener('resize', syncPanelPosition);
onClickOutside(
  triggerRef,
  () => {
    if (!locked.value) visible.value = false;
  },
  { ignore: ['.scope-switch-panel'] },
);
const typeLabels: Record<TenantRelationType, string> = {
  data_brand: 'scopeSwitch.brandView',
  data_shop_group: 'scopeSwitch.groupView',
  data_shop: 'scopeSwitch.shopView',
};
const typeLabel = (type?: string) => proxy.$t(typeLabels[type as TenantRelationType] || 'scopeSwitch.choose');
const currentName = computed(
  () => userStore.userInfoData?.currentRelation?.relationName || userStore.userInfoData?.tenantName || proxy.$t('scopeSwitch.choose'),
);
const currentTypeLabel = computed(() => typeLabel(userStore.userInfoData?.currentRelation?.type));
const entries = computed<ScopeEntry[]>(() =>
  tenants.value.flatMap((tenant) =>
    tenant.brandUserRelationList.map((relation) => ({
      key: JSON.stringify([String(tenant.tenantId), relation.type, String(relation.typeId)]),
      tenant,
      relation,
    }))));
const recentEntries = computed(() =>
  recentKeys.value.map((key) => entries.value.find((entry) => entry.key === key)).filter((entry): entry is ScopeEntry => !!entry));
const groups = computed(() =>
  Object.entries(typeLabels).map(([type, label]) => ({
    type,
    label,
    entries: entries.value.filter(
      (entry) =>
        String(entry.tenant.tenantId) === selectedBrand.value &&
        entry.relation.type === type &&
        entry.relation.relationName.toLocaleLowerCase().includes(searchKeyword.value),
    ),
  })));
const hasMatches = computed(() => groups.value.some((group) => group.entries.length));

const isCurrent = (entry: ScopeEntry) => {
  const current = userStore.userInfoData?.currentRelation;
  if (String(entry.tenant.tenantId) !== String(userStore.tenantId) || entry.relation.type !== current?.type) return false;
  if (current.typeId !== undefined && current.typeId !== null) return String(entry.relation.typeId) === String(current.typeId);
  return current.id !== undefined && entry.relation.id !== undefined && String(entry.relation.id) === String(current.id);
};

const readHistory = () => {
  try {
    const stored: unknown = JSON.parse(localStorage.getItem(SCOPE_SWITCH_RECENT_KEY) || '[]');
    recentKeys.value = Array.isArray(stored) ? [...new Set(stored.filter((key): key is string => typeof key === 'string'))].slice(0, 10) : [];
  } catch {
    recentKeys.value = [];
  }
};
const remember = (entry: ScopeEntry, previousEntry?: ScopeEntry) => {
  // 首次切换时一并记录登录进入的原视角，最新使用的目标视角排在最前。
  recentKeys.value = [...new Set([entry.key, ...(previousEntry ? [previousEntry.key] : []), ...recentEntries.value.map((item) => item.key)])].slice(0, 10);
  try {
    localStorage.setItem(SCOPE_SWITCH_RECENT_KEY, JSON.stringify(recentKeys.value));
  } catch {
    // 浏览器禁用存储时不阻断已成功的视角切换。
  }
};
const selectBrand = (tenant: TenantEntry) => {
  if (locked.value) return;
  if (contentVisible.value && selectedBrand.value === String(tenant.tenantId)) return;
  selectedBrand.value = String(tenant.tenantId);
  keyword.value = '';
  contentVisible.value = true;
};
const selectRecent = () => {
  if (locked.value) return;
  selectedBrand.value = null;
  keyword.value = '';
  contentVisible.value = true;
};
const collapseContent = () => {
  if (!locked.value) contentVisible.value = false;
};
const handleContentHover = (event: MouseEvent) => {
  if (!contentVisible.value || locked.value) return;
  const target = event.target as Element | null;
  if (target?.closest('.scope-switch__brand, .scope-switch__recent-tab, .scope-switch__content')) return;
  const body = event.currentTarget as HTMLElement;
  const activeTab = body.querySelector<HTMLElement>('.scope-switch__brand.is-active, .scope-switch__recent-tab.is-active');
  const content = body.querySelector<HTMLElement>('.scope-switch__content');
  const tabBounds = activeTab?.getBoundingClientRect();
  const contentBounds = content?.getBoundingClientRect();
  // 仅保留当前行到右侧面板之间的横向通道，移入左栏空白立即收起。
  if (
    tabBounds &&
    contentBounds &&
    event.clientX >= tabBounds.right &&
    event.clientX <= contentBounds.left &&
    event.clientY >= tabBounds.top &&
    event.clientY <= tabBounds.bottom
  ) return;
  collapseContent();
};
const applyTenants = (data: TenantEntry[]) => {
  tenants.value = data;
  selectedBrand.value = String(
    data.find((tenant) => String(tenant.tenantId) === String(userStore.tenantId))?.tenantId ?? data[0]?.tenantId ?? '',
  );
  readHistory();
};
const loadTenants = async () => {
  if (loading.value) return;
  loadFailed.value = false;
  const cached = getTenantListCache();
  if (cached !== null) {
    // 命中登录时的列表就直接展示，避免每次打开都发请求或闪现加载态。
    applyTenants(cached);
    return;
  }
  loading.value = true;
  try {
    applyTenants(await getTenantList({ hideErrorNotification: true }));
  } catch {
    loadFailed.value = true;
  } finally {
    loading.value = false;
  }
};
const open = () => {
  if (visible.value) return;
  syncPanelPosition();
  visible.value = true;
  contentVisible.value = false;
  keyword.value = '';
  switchError.value = false;
  void loadTenants();
};
const toggle = () => {
  if (locked.value) return;
  if (visible.value) visible.value = false;
  else open();
};
const beforeClose = (done: () => void) => {
  if (!locked.value) done();
};
const restoreFocus = () => triggerRef.value?.focus();

const reloadWorkspace = () => {
  const route = router.currentRoute.value;
  // 详情、编辑和携带业务参数的页面不跨视角恢复，列表页由路由守卫重新校验权限。
  const unsafe =
    Object.keys(route.query).length > 0 ||
    Object.keys(route.params).length > 0 ||
    route.meta.activeMenu ||
    router.options.routes.some((record) => record.hidden && route.matched.some((matched) => matched.path === record.path));
  if (unsafe) window.location.replace(router.resolve('/').href);
  else window.location.reload();
};
const finishSwitch = async () => {
  switching.value = true;
  let reloadStarted = false;
  try {
    await nextTick();
    switchingOverlayRef.value?.focus();
    const { data } = await getTenantI18nConfig({ hideErrorNotification: true });
    setTenantI18nConfigCache(data);
    reloadWorkspace();
    reloadStarted = true;
  } catch {
    // 后端上下文已变更，保持遮罩与操作锁；只重试配置，不重复提交切换。
    configPending.value = true;
  } finally {
    // 刷新开始后继续遮挡旧视角，交由新文档的启动动画接续。
    switching.value = reloadStarted;
    if (!reloadStarted) {
      await nextTick();
      configRetryRef.value?.$el.focus();
    }
  }
};
const switchTo = async (entry: ScopeEntry) => {
  if (locked.value) return;
  if (isCurrent(entry)) {
    visible.value = false;
    return;
  }
  const focusTarget = document.activeElement as HTMLElement | null;
  switching.value = true;
  switchError.value = false;
  const previousEntry = entries.value.find(isCurrent);
  try {
    await nextTick();
    switchingOverlayRef.value?.focus();
    await tenantWitchApi(entry.relation, { hideErrorNotification: true });
    configPending.value = true;
    remember(entry, previousEntry);
    removeTenantI18nConfigCache();
    await finishSwitch();
  } catch {
    switchError.value = true;
  } finally {
    if (!configPending.value) {
      switching.value = false;
      await nextTick();
      focusTarget?.focus();
    }
  }
};
</script>

<style scoped lang="scss">
.scope-switch-loading {
  position: fixed;
  inset: 0;
  z-index: 1000000;
  display: grid;
  place-items: center;
  background: var(--el-bg-color);
  outline: none;

  &__image {
    width: 120px;
    height: 64px;
  }
}

.scope-switch {
  flex: 0 1 auto;
  min-width: 0;
  margin-inline-start: var(--scope-switch-trigger-offset, 0px);

  &__trigger {
    display: flex;
    align-items: center;
    gap: 8px;
    width: var(--scope-switch-trigger-width);
    max-width: 100%;
    height: 36px;
    padding: 4px 12px 4px 4px;
    border: 1px solid transparent;
    border-radius: 9999px;
    background: var(--el-fill-color-light);
    color: var(--el-text-color-primary);
    text-align: start;
    cursor: pointer;
    font: inherit;
    font-size: 14px;
    line-height: 22px;

    &:hover,
    &.is-open {
      background: var(--el-fill-color);
    }
    &:focus-visible {
      outline: 2px solid var(--el-color-primary);
      outline-offset: 2px;
    }
  }
  &__name {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 14px;
    line-height: 22px;
  }
  &__arrow {
    flex-shrink: 0;
    color: var(--el-text-color-secondary);
    font-size: 12px;
    transition: transform var(--el-transition-duration-fast) var(--el-transition-function-ease-in-out-bezier);
  }
  &__trigger.is-open &__arrow {
    color: var(--el-color-primary);
    transform: rotate(180deg);
  }
}

.scope-switch__mark {
  display: block;
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  object-fit: contain;
}
</style>

<style lang="scss">
.el-overlay.scope-switch-overlay {
  // 下拉层从导航下沿展开，保留入口的再次点击操作。
  top: 64px;
  background: var(--el-overlay-color-lighter);

  .el-overlay-dialog {
    position: absolute;
    display: block;
    height: auto;

    .scope-switch-panel {
      // 覆盖全局对话框的居中 margin，让该面板紧贴导航左侧。
      margin: 0 !important;
      position: relative;
      inset-inline-start: var(--scope-switch-left, 0px);
      padding: 0;
      border-radius: 0 0 12px 12px;
      overflow: hidden;
      background: var(--el-bg-color-overlay);
      box-shadow: var(--el-box-shadow);

      .el-dialog__header {
        margin: 0;
        padding: 0;
      }
      .el-dialog__body {
        padding: 0;
      }
    }
  }

  // 后端已进入切换流程时，遮住导航，避免访问上下文尚未同步的页面。
  &.is-locked {
    top: 0;

    .el-overlay-dialog {
      top: 64px;
    }
  }

  .scope-switch__body {
    display: flex;
    height: min(340px, calc(100vh - 80px));
    height: min(340px, calc(100dvh - 80px));
    color: var(--el-text-color-primary);
    font-size: 14px;
  }
  .scope-switch__brands {
    display: flex;
    flex-direction: column;
    width: var(--scope-switch-brand-width);
    max-width: 100%;
    flex-shrink: 0;
    // 清除全局 aside 的字号、行高和底部间距，品牌行与顶部入口一致。
    margin: 0;
    padding: var(--scope-switch-brand-padding);
    border-radius: 0;
    background: var(--el-bg-color-overlay);
    font: inherit;
    line-height: 22px;
  }
  .scope-switch__brand-list {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
  }
  .scope-switch__brand,
  .scope-switch__recent-tab {
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    min-height: 36px;
    padding: 6px;
    border: 0;
    border-radius: 12px;
    background: transparent;
    color: var(--el-text-color-primary);
    text-align: start;
    cursor: pointer;
    font: inherit;
    &:hover {
      background: var(--el-fill-color-light);
      color: var(--el-color-primary);
    }
    &.is-active {
      background: var(--el-fill-color-light);
      color: var(--el-color-primary);
    }
    > .el-icon:last-child {
      margin-inline-start: auto;
      flex-shrink: 0;
    }
  }
  .scope-switch__brand {
    margin-bottom: 8px;
    padding: 4px 12px 4px 4px;
    border: 1px solid transparent;
    border-radius: 9999px;

    > .scope-switch__label {
      flex: 1;
    }
    > .el-icon:last-child {
      color: var(--el-text-color-secondary);
      font-size: 12px;
    }
    &:hover > .el-icon:last-child,
    &.is-active > .el-icon:last-child {
      color: var(--el-color-primary);
    }
  }
  .scope-switch__recent {
    margin-top: 8px;
    padding-top: 12px;
    border-top: 1px dashed var(--el-border-color);
  }
  .scope-switch__recent-tab {
    padding: 4px 12px 4px 4px;
    border: 1px solid transparent;
    border-radius: 9999px;
    color: var(--el-text-color-secondary);

    > span:not(.scope-switch__recent-mark) {
      flex: 1;
      min-width: 0;
      white-space: normal;
      overflow-wrap: anywhere;
    }
    > .el-icon:last-child {
      font-size: 12px;
    }
    &.is-active {
      color: var(--el-color-primary);
    }
  }
  .scope-switch__recent-mark {
    display: grid;
    place-items: center;
    flex-shrink: 0;
    width: 24px;
    height: 24px;
  }
  .scope-switch__content {
    display: flex;
    flex-direction: column;
    flex: 1;
    min-width: 0;
    padding: 16px;
    padding-inline-end: 66px;
    background: var(--el-fill-color-light);
  }
  .scope-switch__search {
    --el-input-border-radius: 9999px;
    margin-bottom: 24px;
  }
  .scope-switch__results {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
  }
  .scope-switch__group {
    margin-bottom: 32px;
  }
  .scope-switch__group h3,
  .scope-switch__recent-title {
    margin: 0 0 4px;
    color: var(--el-text-color-primary);
    font-size: 14px;
    font-weight: 600;
    line-height: 22px;
  }
  .scope-switch__grid {
    display: flex;
    flex-wrap: wrap;
    gap: 0 16px;

    > .scope-switch__option {
      width: auto;
      max-width: 100%;
    }
  }
  .scope-switch__option {
    display: flex;
    align-items: center;
    gap: 4px;
    min-height: 32px;
    width: 100%;
    min-width: 0;
    padding: 4px 8px;
    border: 1px solid transparent;
    border-radius: 6px;
    background: transparent;
    color: var(--el-text-color-primary);
    text-align: start;
    cursor: pointer;
    font: inherit;
    &:hover {
      background: var(--el-fill-color);
      color: var(--el-color-primary);
    }
    &.is-current {
      background: var(--el-color-primary-light-9);
      color: var(--el-color-primary);
    }
    > .el-icon {
      margin-inline-start: auto;
      flex-shrink: 0;
    }
  }
  button:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
  button:focus-visible {
    outline: 2px solid var(--el-color-primary);
    outline-offset: -2px;
  }
  .scope-switch__label {
    min-width: 0;
    white-space: normal;
    overflow-wrap: anywhere;
  }
  .scope-switch__history {
    margin-bottom: 8px;
    min-height: 52px;
  }
  .scope-switch__history-name {
    display: flex;
    flex-direction: column;
    flex: 1;
    gap: 4px;
    min-width: 0;
  }
  .scope-switch__history-name small {
    color: var(--el-text-color-secondary);
    font-size: 12px;
  }
  .scope-switch__history-type {
    flex-shrink: 0;
    color: var(--el-text-color-secondary);
    font-size: 12px;
  }
  .scope-switch__state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 16px;
    flex: 1;
    width: 100%;
    padding: 24px;
    text-align: center;
    color: var(--el-text-color-secondary);
    line-height: 1.7;
  }
  .scope-switch__body > .el-empty {
    width: 100%;
  }
  .scope-switch__progress {
    display: flex;
    align-items: center;
    gap: 8px;
    padding-top: 12px;
    color: var(--el-color-primary);
  }
  .scope-switch__error {
    margin: 0 0 12px;
    color: var(--el-color-danger);
  }
  .scope-switch__sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip-path: inset(50%);
    white-space: nowrap;
  }
  @media (max-width: 480px) {
    .scope-switch-panel.is-expanded .scope-switch__brands {
      width: 36%;
    }
    .scope-switch__content {
      padding: 12px;
      padding-inline-end: 62px;
    }
    .scope-switch__grid {
      flex-direction: column;

      > .scope-switch__option {
        width: 100%;
      }
    }
    .scope-switch__history {
      flex-wrap: wrap;
    }
  }
}
</style>
